import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { PieChart, TrendingUp, Users, Activity, BarChart3, LineChart } from 'lucide-react';
import { adminApi } from '@/admin/services/api';
import { toast } from 'sonner';

export function AnalyticsDashboard() {
  const [stats, setStats] = useState({
    activeUsers: 0,
    mrr: 0,
    churnRate: 0,
    avgQuoteValue: 0,
    tenantGrowth: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await adminApi.getPlatformAnalytics();
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        toast.error('Failed to load platform analytics');
      }
    } catch (error) {
      toast.error('An error occurred while fetching analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex-1 p-6 lg:p-8 flex items-center justify-center min-h-[500px]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 rounded-xl overflow-hidden border-2 border-purple-500/50 relative animate-pulse">
            <img src="/images/avatars/ravan_ultimate.png" alt="Loading" className="w-full h-full object-cover" />
          </div>
          <p className="text-sm font-semibold tracking-widest uppercase text-muted-foreground">RAVAN Loading Analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium tracking-tight text-foreground">Analytics</h1>
        <p className="text-sm text-muted-foreground">Deep dive into platform usage and growth metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Avg. Quote Value</p>
              <p className="text-2xl font-medium text-foreground">{formatCurrency(stats.avgQuoteValue)}</p>
              <p className="text-xs font-medium text-emerald-400 mt-1 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Historical Avg
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Tenant Growth</p>
              <p className="text-2xl font-medium text-foreground">+{stats.tenantGrowth}</p>
              <p className="text-xs font-medium text-emerald-400 mt-1 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Total active tenants
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Active Users</p>
              <p className="text-2xl font-medium text-foreground">{stats.activeUsers}</p>
              <p className="text-xs font-medium text-muted-foreground mt-1">Across all tenants</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Churn Rate</p>
              <p className="text-2xl font-medium text-foreground">{stats.churnRate.toFixed(1)}%</p>
              <p className="text-xs font-medium text-emerald-400 mt-1">Overall Risk</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4">
        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-foreground flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-400" />
              Revenue Over Time
            </CardTitle>
            <CardDescription className="text-muted-foreground">MRR growth for the current year</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center border-t border-slate-800 mt-4">
              <div className="text-center">
                <LineChart className="h-8 w-8 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Data visualization coming soon</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-foreground flex items-center gap-2">
              <PieChart className="h-5 w-5 text-purple-400" />
              Tenant Plan Distribution
            </CardTitle>
            <CardDescription className="text-muted-foreground">Breakdown of workspaces by subscription tier</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center border-t border-slate-800 mt-4">
              <div className="text-center">
                <PieChart className="h-8 w-8 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Chart rendering coming soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
